import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import {
	BILL_PAYMENT_PAID_WARNINGS,
	BILL_PAYMENT_REFERENCE_MAX_LENGTH,
	BILL_PAYMENT_STATUS_LABELS,
	blocksSettlingBill,
	canForgetBillPayment,
	canResendBillPayment,
	canSendBillAgain,
	isBillPaymentInFlight,
	type BillPayment,
	type BillPaymentAnswer,
	type BillPaymentRequest,
	type BillPaymentStatus,
	type BillPaymentVendor,
	type PaymentDocument,
} from "@ravnar/api-contract";

import {
	billPaymentQueryOptions,
	deleteBillPayment,
	forgetBillPayment,
	sendBillPayment,
} from "../../../api/bill-payments";
import { useAskAgain } from "../../../api/refresh";
import { useSession } from "../../../api/session";
import { Button } from "../../../components/ui/button";
import { Field, Input, Select } from "../../../components/ui/field";
import { Pill } from "../../../components/ui/pill";
import { Skeleton } from "../../../components/ui/skeleton";
import { BILL_PAYMENT_TONES } from "../../../domain/payments";
import { formatAmount, formatRelativeTime } from "../../../lib/format";
import { showToast } from "../../../lib/toast";
import { DrawerSection } from "./drawer-section";

/** What each status of a draft means for the bill, under its pill. */
const STATUS_TEXT: Readonly<Record<BillPaymentStatus, string>> = {
	sending:
		"Revolut did not answer when it was sent, or not in words Ravnar could read. Send it again within 23 hours of its first sending: Revolut makes no second draft of it.",
	awaiting: "It waits in Revolut until the business's owner approves it there.",
	approved: "The owner approved it in Revolut, which is making the transfer.",
	completed: "Revolut made the transfer. The bill is paid once reconciliation matches the transfer to it.",
	failed: "It was not paid: Revolut refused the draft, or the transfer failed.",
	deleted: "The draft was deleted.",
};

/**
 * Paying a vendor's bill with Revolut, in its drawer: where its draft payment stands, and for an editor of Finance the
 * way to send it — to a vendor the bill is linked to, at the account Ravnar keeps for it — or to send it again once its
 * draft failed or was deleted, to delete a draft that waits, or to forget one Ravnar can no longer follow. A paid or
 * settled bill never sent shows nothing.
 */
export function PayWithRevolut({ document }: { readonly document: PaymentDocument }) {
	const { data, isPending, isError, error, dataUpdatedAt } = useQuery(billPaymentQueryOptions(document.fileId));
	const unpaid = document.status === "open" || document.status === "overdue";
	if (!unpaid && data?.billPayment === null) {
		return null;
	}
	return (
		<DrawerSection title="Pay with Revolut">
			{isPending ? (
				<div role="status" aria-label="Loading the payment" className="flex flex-col gap-2">
					<Skeleton className="h-3.5 w-2/3" />
					<Skeleton className="h-3.5 w-1/2" />
				</div>
			) : isError ? (
				<p role="alert" className="text-caption text-alert">
					{error.message}
				</p>
			) : (
				<BillPaymentBody answer={data} document={document} unpaid={unpaid} answeredAt={dataUpdatedAt} />
			)}
		</DrawerSection>
	);
}

/**
 * The draft sent for the bill, or the way to send one, and asking the audit log again as the entry arrives. An answer
 * the API refused asks for the bill's draft again: it may stand otherwise than the drawer read it.
 */
function useBillPayment(fileId: string) {
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const { queryKey } = billPaymentQueryOptions(fileId);
	const send = useMutation({
		mutationFn: (request: BillPaymentRequest) => sendBillPayment(request),
		onSuccess: async answer => {
			queryClient.setQueryData(queryKey, answer);
			if (answer.billPayment?.status !== "failed") {
				showToast("Bill sent to Revolut.");
			}
			await askAgain(["settings", "audit-log"]);
		},
		onError: () => queryClient.invalidateQueries({ queryKey }),
	});
	const remove = useMutation({
		mutationFn: () => deleteBillPayment(fileId),
		onSuccess: async () => {
			await Promise.all([queryClient.invalidateQueries({ queryKey }), askAgain(["settings", "audit-log"])]);
			showToast("Draft deleted.");
		},
		onError: () => queryClient.invalidateQueries({ queryKey }),
	});
	const forget = useMutation({
		mutationFn: () => forgetBillPayment(fileId),
		onSuccess: async () => {
			await Promise.all([queryClient.invalidateQueries({ queryKey }), askAgain(["settings", "audit-log"])]);
			showToast("Draft forgotten.");
		},
		onError: () => queryClient.invalidateQueries({ queryKey }),
	});
	return { send, remove, forget };
}

function BillPaymentBody({
	answer,
	document,
	unpaid,
	answeredAt,
}: {
	readonly answer: BillPaymentAnswer;
	readonly document: PaymentDocument;
	readonly unpaid: boolean;
	/** When the API answered, in milliseconds: the moment what may be done with the draft is read for. */
	readonly answeredAt: number;
}) {
	const { member } = useSession();
	const canEdit = member.access.finance === "editor";
	const { billPayment, followed } = answer;
	const { send, remove, forget } = useBillPayment(document.fileId);
	const sendable = unpaid && (billPayment === null || canSendBillAgain(billPayment.status));
	return (
		<div className="flex flex-col gap-3">
			{!unpaid && billPayment !== null && blocksSettlingBill(billPayment.status) ? (
				<p className="text-caption text-warn">{BILL_PAYMENT_PAID_WARNINGS[billPayment.status]}</p>
			) : null}
			{billPayment === null ? null : (
				<DraftFacts
					payment={billPayment}
					now={answeredAt}
					followed={followed}
					canEdit={canEdit}
					busy={send.isPending || remove.isPending || forget.isPending}
					onSendAgain={() => {
						const { fileId, vendorId, reference } = billPayment;
						send.mutate({ fileId, vendorId, reference });
					}}
					onDelete={() => {
						remove.mutate();
					}}
					onForget={() => {
						forget.mutate();
					}}
				/>
			)}
			{sendable && canEdit ? (
				<SendToRevolut
					answer={answer}
					document={document}
					again={billPayment !== null}
					busy={send.isPending}
					onSend={request => {
						send.mutate(request);
					}}
				/>
			) : null}
			{sendable && !canEdit && billPayment === null ? (
				<p className="text-caption text-ink-3">Not sent to Revolut.</p>
			) : null}
			{(send.error ?? remove.error ?? forget.error) === null ? null : (
				<p role="alert" className="text-caption text-alert">
					{(send.error ?? remove.error ?? forget.error)?.message}
				</p>
			)}
		</div>
	);
}

/**
 * Where the bill's draft stands, to whom and how much, who sent it when, and what an editor may do with it now: send it
 * again or delete it while Ravnar can follow it in Revolut, or forget it once Ravnar cannot — never while a sending of it
 * may still be answered. Deleting asks nothing first, since the bill may be sent again, as Reopen takes a settlement
 * back; forgetting asks nothing either, but says beside its button what to look at in Revolut first, since it cannot be
 * undone. A confirming dialog opened from inside the drawer would close the drawer with it: React hands a `<dialog>`'s
 * close event to the ones around it too.
 */
function DraftFacts({
	payment,
	followed,
	now,
	canEdit,
	busy,
	onSendAgain,
	onDelete,
	onForget,
}: {
	readonly payment: BillPayment;
	readonly followed: boolean;
	/** The moment what may be done is read for: when the API answered, so a minute or a day passes as it asks again. */
	readonly now: number;
	readonly canEdit: boolean;
	readonly busy: boolean;
	readonly onSendAgain: () => void;
	readonly onDelete: () => void;
	readonly onForget: () => void;
}) {
	const { vendorName, account, amount, reference, status, sentBy, sentAt, failedReason } = payment;
	const inFlight = isBillPaymentInFlight(payment, now);
	const keyKept = canResendBillPayment(payment, now);
	const forgettable = canForgetBillPayment(payment, followed, now);
	return (
		<div className="flex flex-col gap-1.5">
			<div className="flex flex-wrap items-center gap-2">
				<Pill tone={BILL_PAYMENT_TONES[status]}>{BILL_PAYMENT_STATUS_LABELS[status]}</Pill>
				<span className="text-caption text-ink-3">{`Sent by ${sentBy}, ${formatRelativeTime(sentAt)}`}</span>
			</div>
			<p className="text-body text-ink">
				{`${formatAmount(amount.amount, amount.currency)} to ${vendorName} · ${account}`}
			</p>
			<p className="text-caption text-ink-3">{`Reference ${reference}`}</p>
			<p className="text-caption text-ink-2">{STATUS_TEXT[status]}</p>
			{failedReason === undefined ? null : <p className="text-caption text-alert">{failedReason}</p>}
			{inFlight ? (
				<p className="text-caption text-ink-3">It is being sent right now: look at it again in a minute.</p>
			) : null}
			{status === "sending" && !inFlight && !keyKept ? (
				<p className="text-caption text-warn">
					It was first sent over 23 hours ago, and Revolut may no longer know its key, so it is not sent
					again: look for it in Revolut Business&apos;s drafts.
				</p>
			) : null}
			{!followed && blocksSettlingBill(status) ? (
				<p className="text-caption text-warn">
					Ravnar can no longer ask Revolut about this draft: payments were stopped, refused or allowed again
					since it was sent.
				</p>
			) : null}
			{canEdit && followed && keyKept ? (
				<div>
					<Button size="sm" disabled={busy} onClick={onSendAgain}>
						Send again
					</Button>
				</div>
			) : null}
			{canEdit && status === "awaiting" && followed ? (
				<div className="flex items-center gap-3">
					<Button size="sm" disabled={busy} onClick={onDelete}>
						Delete draft
					</Button>
					<span className="text-caption text-ink-3">
						The owner no longer sees it, and it may be sent again.
					</span>
				</div>
			) : null}
			{canEdit && forgettable ? (
				<div className="flex items-center gap-3">
					<Button size="sm" disabled={busy} onClick={onForget}>
						Forget draft
					</Button>
					<span className="text-caption text-ink-3">
						{status === "approved"
							? "Look in Revolut Business's drafts and transfers first: Revolut may be making this transfer, and forgetting it here stops nothing there."
							: "Look for it in Revolut Business's drafts first: forgetting it here deletes nothing there."}
					</span>
				</div>
			) : null}
		</div>
	);
}

/**
 * The way to send the bill to Revolut, or why it cannot be sent: payments not allowed, no vendor linked, or none with a
 * bank account. The bill's number, or its file's name, is the reference to start with.
 */
function SendToRevolut({
	answer,
	document,
	again,
	busy,
	onSend,
}: {
	readonly answer: BillPaymentAnswer;
	readonly document: PaymentDocument;
	readonly again: boolean;
	readonly busy: boolean;
	readonly onSend: (request: BillPaymentRequest) => void;
}) {
	const { payments, vendors } = answer;
	if (payments.status === "unavailable") {
		return <p className="text-caption text-ink-3">Revolut is not set up for this deployment of Ravnar.</p>;
	}
	if (payments.status === "not-connected" || payments.status === "not-allowed") {
		return (
			<p className="text-caption text-ink-3">
				Revolut payments are not allowed. An owner or admin allows them on the Revolut page in Settings.
			</p>
		);
	}
	if (payments.status === "reconnect") {
		return (
			<p className="text-caption text-warn">
				Revolut no longer accepts Ravnar&apos;s drafts. An owner or admin allows payments again on the Revolut
				page in Settings.
			</p>
		);
	}
	if (vendors.length === 0) {
		return (
			<p className="text-caption text-ink-3">
				Link this bill to its vendor on{" "}
				<Link to="/intake/$fileId" params={{ fileId: document.fileId }} className="text-ink hover:underline">
					its page in Intake
				</Link>{" "}
				first.
			</p>
		);
	}
	const payable = vendors.filter(vendor => vendor.account !== undefined);
	const [first] = payable;
	if (first === undefined) {
		return (
			<p className="text-caption text-ink-3">
				{`${vendors.map(vendor => vendor.name).join(", ")} has no bank account yet. An editor of Finance adds it on the vendor's page in the Directory.`}
			</p>
		);
	}
	return <SendForm vendors={payable} first={first} document={document} again={again} busy={busy} onSend={onSend} />;
}

function SendForm({
	vendors,
	first,
	document,
	again,
	busy,
	onSend,
}: {
	readonly vendors: readonly BillPaymentVendor[];
	readonly first: BillPaymentVendor;
	readonly document: PaymentDocument;
	readonly again: boolean;
	readonly busy: boolean;
	readonly onSend: (request: BillPaymentRequest) => void;
}) {
	const [vendorId, setVendorId] = useState(first.vendorId);
	const [reference, setReference] = useState(
		(document.number ?? document.fileName).slice(0, BILL_PAYMENT_REFERENCE_MAX_LENGTH),
	);
	const { total } = document;
	return (
		<form
			className="flex flex-col gap-2.5"
			onSubmit={event => {
				event.preventDefault();
				onSend({ fileId: document.fileId, vendorId, reference: reference.trim() });
			}}
		>
			{vendors.length > 1 ? (
				<Field label="Pay to">
					<Select
						value={vendorId}
						onChange={event => {
							setVendorId(event.target.value);
						}}
					>
						{vendors.map(vendor => (
							<option key={vendor.vendorId} value={vendor.vendorId}>
								{`${vendor.name} · ${vendor.account ?? ""}`}
							</option>
						))}
					</Select>
				</Field>
			) : (
				<p className="text-body text-ink">{`To ${first.name} · ${first.account ?? ""}`}</p>
			)}
			<Field label="Reference">
				<Input
					value={reference}
					maxLength={BILL_PAYMENT_REFERENCE_MAX_LENGTH}
					onChange={event => {
						setReference(event.target.value);
					}}
				/>
			</Field>
			<p className="text-caption text-ink-3">
				{`${formatAmount(total.amount, total.currency)} as a draft in Revolut, which the business's owner approves there. The bill is paid once reconciliation matches the transfer to it.`}
			</p>
			<div>
				<Button type="submit" size="sm" variant="primary" disabled={busy || reference.trim() === ""}>
					{again ? "Send again" : "Send to Revolut"}
				</Button>
			</div>
		</form>
	);
}
